import { useEffect, useMemo, useState } from "react";
import { Mail, RefreshCcw, Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  listEmailThreads,
  listThreadMessages,
  listEmailThreadComments,
  createEmailThreadComment,
  dismissEmailThread,
  linkEmailContact,
} from "@/api/email";
import { useEmailStatus } from "@/hooks/useEmailStatus";
import type { EmailThreadSummary, EmailThreadMessage, EmailThreadComment } from "@/types/email";
import type { Task } from "@/types/projects";
import type { CrmContact, CrmLead } from "@/types/crm";
import { createGoal, createGoalTask, fetchGoals } from "@/api/projects";
import { createLeadFromEmailThread, fetchContact, fetchContacts, fetchLeadDetail, linkLeadEmailThread } from "@/api/crm";
import { createManualBookingEvent } from "@/api/booking";
import { usePageBreadcrumbs } from "@/providers/BreadcrumbProvider";
import PageHeader from "@/components/PageHeader";
import { EmailThreadListItem } from "@/components/email/EmailThreadListItem";
import { EmailThreadView } from "@/components/email/EmailThreadView";
import { EmailComposerDrawer } from "@/components/email/EmailComposerDrawer";
import { TemplateManagerDrawer } from "@/components/email/TemplateManagerDrawer";
import { useToast } from "@/components/ToastProvider";
import EmailStatusBanner from "@/components/email/EmailStatusBanner";
import { LeadSearchModal } from "@/components/leads/LeadSearchModal";
import { type BookingEventFormState, type BookingEventSubmission } from "@/components/booking/BookingEventForm";
import { BookingEventDrawer } from "@/components/booking/BookingEventDrawer";
import { Button } from "@/components/ui/button";

type ThreadTab = "inbox" | "sent" | "all";

export default function EmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  usePageBreadcrumbs([
    { label: "Contacts", href: "/contacts" },
    { label: "Email" },
  ]);

  const { push } = useToast();
  const { status: emailStatus } = useEmailStatus();
  const [threads, setThreads] = useState<EmailThreadSummary[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<ThreadTab>("inbox");
  const [searchValue, setSearchValue] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<EmailThreadMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [comments, setComments] = useState<EmailThreadComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [tasksByThread, setTasksByThread] = useState<Record<string, Task[]>>({});
  const [dismissingThreads, setDismissingThreads] = useState<Record<string, boolean>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState<"new" | "reply" | "forward">("new");
  const [composerInitial, setComposerInitial] = useState<{ to?: string; subject?: string; body_html?: string }>({});
  const [selectedLead, setSelectedLead] = useState<CrmLead | null>(null);
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [filteredLeadId, setFilteredLeadId] = useState<number | null>(null);
  const [filteredContactId, setFilteredContactId] = useState<number | null>(null);
  const [selectedContact, setSelectedContact] = useState<CrmContact | null>(null);
  const [threadContact, setThreadContact] = useState<CrmContact | null>(null);
  const [leadCache, setLeadCache] = useState<Record<number, CrmLead>>({});
  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState<CrmContact[]>([]);
  const [contactSearching, setContactSearching] = useState(false);
  const [contactLinking, setContactLinking] = useState(false);
  const [contactLinkSuccess, setContactLinkSuccess] = useState(false);
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [eventInitialValues, setEventInitialValues] = useState<Partial<BookingEventFormState> | null>(null);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);

  const selectedThread = useMemo(
    () => threads.find((thread) => thread.id === selectedThreadId) ?? null,
    [threads, selectedThreadId],
  );

  const linkedTasks: Task[] = useMemo(
    () => (selectedThreadId ? tasksByThread[selectedThreadId] ?? [] : []),
    [selectedThreadId, tasksByThread],
  );

  const refreshThreads = async () => {
    setThreadsLoading(true);
    try {
      const payload = await listEmailThreads({
        folder: activeTab,
        search: searchApplied || undefined,
        limit: 100,
      });
      const allThreads = payload ?? [];
      setThreads(allThreads);
      let currentSelectedId = selectedThreadId;
      // If the previously selected thread no longer exists, clear selection.
      if (currentSelectedId && !allThreads.some((thread) => thread.id === currentSelectedId)) {
        currentSelectedId = null;
        setSelectedThreadId(null);
        setMessages([]);
        setComments([]);
      }
      // Smart auto-selection for initial load / when selection was cleared.
      if (!currentSelectedId && allThreads.length) {
        let candidate: string | null = null;
        if (filteredLeadId) {
          const match = allThreads.find((thread) => thread.lead_id === filteredLeadId);
          candidate = match?.id ?? null;
        } else if (filteredContactId) {
          const match = allThreads.find((thread) => thread.contact_id === filteredContactId);
          candidate = match?.id ?? null;
        } else {
          const unrepliedInbound = allThreads.find(
            (thread) => thread.direction === "inbound" && thread.unreplied,
          );
          if (unrepliedInbound) {
            candidate = unrepliedInbound.id;
          } else {
            candidate = allThreads[0]?.id ?? null;
          }
        }
        if (candidate) {
          setSelectedThreadId(candidate);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load email threads.";
      push({ title: "Email threads", message });
    } finally {
      setThreadsLoading(false);
    }
  };

  useEffect(() => {
    if (emailStatus?.status === "not_configured") {
      setThreads([]);
      setSelectedThreadId(null);
      return;
    }
    const params = new URLSearchParams(location.search || "");
    const leadIdParam = params.get("leadId");
    const contactIdParam = params.get("contactId");
    if (leadIdParam) {
      const parsed = Number(leadIdParam);
      if (Number.isFinite(parsed)) {
        setFilteredLeadId(parsed);
        setFilteredContactId(null);
      }
    } else if (contactIdParam) {
      const parsed = Number(contactIdParam);
      if (Number.isFinite(parsed)) {
        setFilteredContactId(parsed);
        setFilteredLeadId(null);
      }
    } else {
      setFilteredLeadId(null);
      setFilteredContactId(null);
    }
    void refreshThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchApplied, location.search, emailStatus]);


  useEffect(() => {
    if (!selectedThreadId || emailStatus?.status === "not_configured") return;
    setMessagesLoading(true);
    setCommentsLoading(true);
    void (async () => {
      try {
        const [messagePayload, commentPayload] = await Promise.all([
          listThreadMessages(selectedThreadId),
          listEmailThreadComments(selectedThreadId),
        ]);
        setMessages(messagePayload ?? []);
        setComments(commentPayload ?? []);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to load thread details.";
        push({ title: "Email thread", message });
      } finally {
        setMessagesLoading(false);
        setCommentsLoading(false);
      }
    })();
  }, [selectedThreadId, emailStatus, push]);

  useEffect(() => {
    const leadId = selectedThread?.lead_id;
    if (!leadId) {
      setSelectedLead(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const lead = await fetchLeadDetail(leadId);
        if (!cancelled) {
          setSelectedLead(lead);
          setLeadCache((prev) => (lead.id in prev ? prev : { ...prev, [lead.id]: lead }));
        }
      } catch {
        if (!cancelled) {
          setSelectedLead(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedThread?.lead_id]);

  useEffect(() => {
    if (!filteredContactId) {
      setSelectedContact(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const contact = await fetchContact(filteredContactId);
        if (!cancelled) {
          setSelectedContact(contact);
        }
      } catch {
        if (!cancelled) {
          setSelectedContact(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filteredContactId]);

  useEffect(() => {
    const contactId = selectedThread?.contact_id;
    if (!contactId) {
      setThreadContact(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const contact = await fetchContact(contactId);
        if (!cancelled) {
          setThreadContact(contact);
        }
      } catch {
        if (!cancelled) {
          setThreadContact(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedThread?.contact_id]);

  useEffect(() => {
    const parsed = selectedThread ? pickThreadContact(selectedThread, messages) : null;
    const seed = parsed?.email || parsed?.name || "";
    setContactSearch(seed);
    setContactResults([]);
    setContactSearching(false);
    setContactLinkSuccess(false);
  }, [selectedThreadId]);

  useEffect(() => {
    if (!selectedThreadId || !contactSearch.trim()) {
      setContactResults([]);
      return;
    }
    let cancelled = false;
    setContactSearching(true);
    const handle = setTimeout(async () => {
      try {
        const result = await fetchContacts({ query: contactSearch.trim(), limit: 6 });
        if (!cancelled) {
          setContactResults(result.contacts);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Unable to search contacts.";
          push({ title: "Contact search", message });
        }
      } finally {
        if (!cancelled) {
          setContactSearching(false);
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
      setContactSearching(false);
    };
  }, [contactSearch, selectedThreadId, push]);

  useEffect(() => {
    const leadIds = Array.from(
      new Set(
        threads
          .map((thread) => thread.lead_id)
          .filter((value): value is number => typeof value === "number" && Number.isFinite(value)),
      ),
    );
    const missing = leadIds.filter((id) => !(id in leadCache));
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      for (const id of missing) {
        try {
          const lead = await fetchLeadDetail(id);
          if (cancelled) return;
          setLeadCache((prev) => (id in prev ? prev : { ...prev, [id]: lead }));
        } catch {
          if (cancelled) return;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [threads, leadCache]);

  const handleSelectThread = (threadId: string) => {
    setSelectedThreadId(threadId);
  };

  const handleComposeNew = () => {
    setComposerMode("new");
    setComposerInitial({});
    setComposerOpen(true);
  };

  const handleReply = () => {
    if (!selectedThread || messages.length === 0) {
      handleComposeNew();
      return;
    }
    const last = messages[messages.length - 1];
    setComposerMode("reply");
    setComposerInitial({
      to: last.from_address || selectedThread.participants[0],
      subject: last.subject?.startsWith("Re:") ? last.subject : `Re: ${last.subject}`,
      body_html: `<p></p><hr /><p>On ${formatTimestamp(
        last.sent_at || last.created_at,
      )}, ${last.from_address || "they"} wrote:</p>${last.body_html ?? ""}`,
    });
    setComposerOpen(true);
  };

  const handleForward = () => {
    if (!selectedThread || messages.length === 0) {
      handleComposeNew();
      return;
    }
    const last = messages[messages.length - 1];
    setComposerMode("forward");
    setComposerInitial({
      to: "",
      subject: last.subject?.startsWith("Fwd:") ? last.subject : `Fwd: ${last.subject}`,
      body_html: `<p></p><hr /><p>Forwarded message:</p>${last.body_html ?? ""}`,
    });
    setComposerOpen(true);
  };

  const handleAddComment = async (text: string) => {
    if (!selectedThreadId) return;
    try {
      const created = await createEmailThreadComment(selectedThreadId, { text });
      setComments((prev) => [...prev, created]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to add comment.";
      push({ title: "Comment", message });
    }
  };

  const handleDismissThread = async (threadId: string) => {
    setDismissingThreads((prev) => ({ ...prev, [threadId]: true }));
    try {
      await dismissEmailThread(threadId);
      if (selectedThreadId === threadId) {
        setSelectedThreadId(null);
        setMessages([]);
        setComments([]);
      }
      await refreshThreads();
      push({ title: "Email dismissed", message: "Removed from the inbox queue." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to dismiss thread.";
      push({ title: "Dismiss email", message });
    } finally {
      setDismissingThreads((prev) => {
        const { [threadId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCreateTaskFromThread = async () => {
    if (!selectedThread) return;
    try {
      const goals = await fetchGoals();
      let goalId = goals.find((goal) => goal.status === "active")?.id ?? goals[0]?.id;
      if (!goalId) {
        const createdGoal = await createGoal({
          title: "Email follow-ups",
          description: "Tasks created from manual email threads.",
          priority: "medium",
          status: "active",
        });
        goalId = createdGoal.id;
      }
      const participantLabel = selectedThread.participants?.[0] ?? "contact";
      const title = `Follow up with ${participantLabel} re: ${selectedThread.subject || "email thread"}`;
      const task = await createGoalTask(goalId, {
        title,
        detail: `Created from email thread "${selectedThread.subject || "Untitled"}".`,
        status: "todo",
        priority: "medium",
        source_agent: "manual_email",
      });
      setTasksByThread((prev) => {
        const existing = prev[selectedThread.id] ?? [];
        return { ...prev, [selectedThread.id]: [...existing, task] };
      });
      push({ title: "Task created", message: `Task "${task.title}" linked to this thread.` });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to create task from thread.";
      push({ title: "Task creation", message });
    }
  };

  const openContactWorkspace = (url: string) => {
    navigate(url);
    if (typeof window === "undefined") return;
    const targetPath = url.split("?")[0];
    window.setTimeout(() => {
      if (window.location.pathname !== targetPath) return;
      if (window.location.pathname === "/email") {
        window.location.assign(url);
      }
    }, 50);
  };

  const handleAddToCrmFromThread = async (thread: EmailThreadSummary) => {
    const parsed = parseMailboxAddress(thread.participants?.[0] ?? "");
    if (!parsed.email) {
      push({ title: "Add to CRM", message: "No email address found for this thread." });
      return;
    }
    let logId: string | null = null;
    try {
      const threadMessages = await listThreadMessages(thread.id);
      const target = findLinkableMessage(threadMessages ?? []);
      logId = target?.id ?? null;
    } catch {
      // best-effort; proceed without log link
    }
    const params = new URLSearchParams();
    params.set("newContact", "1");
    params.set("email", parsed.email);
    if (parsed.name) params.set("name", parsed.name);
    params.set("source", "inbox");
    if (logId) params.set("logId", logId);
    params.set("token", String(Date.now()));
    openContactWorkspace(`/contacts?${params.toString()}`);
  };

  const handleCreateContactFromThread = (candidate?: ParsedAddress) => {
    if (!selectedThread) return;
    const parsed = candidate ?? pickThreadContact(selectedThread, messages);
    if (!parsed.email) {
      push({ title: "Add to CRM", message: "No sender email found in this thread." });
      return;
    }
    const target = findLinkableMessage(messages);
    const params = new URLSearchParams();
    params.set("newContact", "1");
    params.set("email", parsed.email);
    if (parsed.name) params.set("name", parsed.name);
    params.set("source", "thread");
    if (target?.id) params.set("logId", target.id);
    params.set("token", String(Date.now()));
    openContactWorkspace(`/contacts?${params.toString()}`);
  };

  const handleLinkExistingContact = async (contactId: number) => {
    if (!selectedThread || !messages.length) return;
    const target = findLinkableMessage(messages);
    if (!target?.id) {
      push({ title: "Link contact", message: "Unable to identify a message to attach." });
      return;
    }
    setContactLinking(true);
    try {
      await linkEmailContact(target.id, { contact_id: contactId });
      const cached = contactResults.find((contact) => contact.id === contactId) ?? null;
      if (cached) {
        setThreadContact(cached);
      } else {
        try {
          const contact = await fetchContact(contactId);
          setThreadContact(contact);
        } catch {
          // best-effort
        }
      }
      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === selectedThread.id ? { ...thread, contact_id: contactId } : thread,
        ),
      );
      setContactLinkSuccess(true);
      push({ title: "Contact linked", message: `Thread linked to contact #${contactId}.` });
      void refreshThreads();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to link contact.";
      push({ title: "Link contact", message });
    } finally {
      setContactLinking(false);
    }
  };

  const handleOpenEventDrawer = () => {
    if (!selectedThread) return;
    const parsed = pickThreadContact(selectedThread, messages);
    setEventInitialValues({
      event_name: selectedThread.subject || undefined,
      contact_name: parsed.name || undefined,
      contact_email: parsed.email || undefined,
      lead_id: selectedLead?.id,
      notes: selectedThread.subject ? `Sourced from email thread: ${selectedThread.subject}` : "Sourced from email thread.",
      metadata: {
        source: "email_thread",
        thread_id: selectedThread.id,
        lead_id: selectedLead?.id ?? null,
      },
    });
    setEventDrawerOpen(true);
  };

  const handleCreateEvent = async (values: BookingEventSubmission) => {
    setEventSubmitting(true);
    try {
      const metadata = {
        ...(values.metadata ?? {}),
        source: "email_thread",
        thread_id: selectedThread?.id ?? null,
      };
      await createManualBookingEvent({ ...values, metadata });
      push({ title: "Show created", message: "Booking event saved from this thread." });
      setEventDrawerOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to create show from thread.";
      push({ title: "Create event", message });
    } finally {
      setEventSubmitting(false);
    }
  };

  const filteredThreads = useMemo(() => {
    if (filteredLeadId) {
      return threads.filter((thread) => thread.lead_id === filteredLeadId);
    }
    if (filteredContactId) {
      return threads.filter((thread) => thread.contact_id === filteredContactId);
    }
    return threads;
  }, [threads, filteredLeadId, filteredContactId]);

  const activeComposerContactId = selectedThread?.contact_id ?? null;
  const activeComposerLeadId = selectedThread?.lead_id ?? null;
  const threadContactCandidates = useMemo(() => {
    if (!selectedThread) return [];
    const normalized = new Map<string, ParsedAddress>();

    const addAddress = (raw?: string | null) => {
      const parsed = parseMailboxAddress(raw);
      const email = parsed.email?.trim().toLowerCase();
      if (!email || normalized.has(email)) return;
      normalized.set(email, parsed);
    };

    for (const message of messages) {
      if (message.direction === "inbound") {
        addAddress(message.from_address);
      }
    }

    if (!normalized.size) {
      const lastOutbound = [...messages].reverse().find((message) => message.direction === "outbound");
      if (lastOutbound?.to_address) {
        addAddress(lastOutbound.to_address);
      } else {
        addAddress(selectedThread.participants?.[0] ?? null);
      }
    }

    return Array.from(normalized.values());
  }, [messages, selectedThread]);

  return (
    <div className="aa-page-shell space-y-3">
      <EmailStatusBanner status={emailStatus} />

      <section className="space-y-2 px-1 py-1">
        <div className="flex flex-nowrap items-center gap-2 overflow-x-auto text-xs">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Refresh inbox"
            onClick={() => void refreshThreads()}
            className="h-7 w-7 rounded-full"
            disabled={threadsLoading || emailStatus?.status === "not_configured"}
          >
            <RefreshCcw className="h-3.5 w-3.5" />
          </Button>
          {(["inbox", "sent", "all"] as ThreadTab[]).map((tab) => (
            <Button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              variant={activeTab === tab ? "secondary" : "ghost"}
              size="sm"
              className="shrink-0 rounded-full px-3 text-[11px]"
            >
              {tab === "inbox" ? "Inbox" : tab === "sent" ? "Sent" : "All"}
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setTemplateManagerOpen(true)}
            className="shrink-0 rounded-full border-border/70 bg-background/40 uppercase tracking-wide text-[11px]"
          >
            Templates
          </Button>
        </div>
        <div className="flex flex-1 min-w-[20rem] items-center gap-2">
          <form
            className="flex min-w-[18rem] flex-1 items-center gap-2 rounded-full border border-border bg-background/40 px-3 py-1.5 text-xs text-foreground/80"
            onSubmit={(event) => {
              event.preventDefault();
              setSearchApplied(searchValue.trim());
            }}
          >
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search subject, contact, or body…"
              className="h-6 flex-1 bg-transparent text-[11px] outline-none"
            />
          </form>
          <Button
            type="button"
            onClick={handleComposeNew}
            size="sm"
            variant="outline"
            className="shrink-0 rounded-full border-border/70 bg-background/40 uppercase tracking-wide text-[11px]"
          >
            Compose
          </Button>
        </div>
      </section>
      <div className="flex flex-wrap items-center gap-2 px-1">
        {filteredLeadId && selectedLead && (
          <div className="flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] text-primary">
            <span>
              Filtering by Lead {selectedLead.title || selectedLead.id} (#{selectedLead.id})
            </span>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto px-0 text-[11px]"
              onClick={() => {
                setFilteredLeadId(null);
                navigate("/email", { replace: true });
              }}
            >
              Clear
            </Button>
          </div>
        )}
        {filteredContactId && selectedContact && (
          <div className="flex items-center gap-2 rounded-full border border-sky-400/60 bg-sky-500/10 px-3 py-1 text-[11px] text-sky-100">
            <span>
              Filtering by Contact{" "}
              {selectedContact.name || selectedContact.company_name || selectedContact.id} (#
              {selectedContact.id})
            </span>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto px-0 text-[11px]"
              onClick={() => {
                setFilteredContactId(null);
                navigate("/email", { replace: true });
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <aside className="flex h-[70vh] min-h-[28rem] flex-col gap-3 rounded-xl border border-border bg-background/60 p-3">
          <div className="flex items-center justify-between gap-2 px-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Threads</p>
            <p className="text-[11px] text-muted-foreground">
              {threadsLoading ? "Loading…" : `${filteredThreads.length} conversations`}
            </p>
          </div>
          {threadsLoading && !threads.length ? (
            <p className="px-1 text-xs text-muted-foreground">Loading email threads…</p>
          ) : filteredThreads.length === 0 ? (
            <p className="px-1 text-xs text-muted-foreground">
              No email activity yet. Use Compose to send a first message or connect your mailbox.
            </p>
          ) : (
            <div className="flex-1 space-y-2 overflow-y-auto pr-1">
              {filteredThreads.map((thread) => {
                const leadMeta =
                  typeof thread.lead_id === "number" && Number.isFinite(thread.lead_id)
                    ? leadCache[thread.lead_id] || undefined
                    : undefined;
                const highlighted =
                  (filteredLeadId && thread.lead_id === filteredLeadId) ||
                  (filteredContactId && thread.contact_id === filteredContactId);
                return (
                  <EmailThreadListItem
                    key={thread.id}
                    thread={thread}
                    isActive={thread.id === selectedThreadId}
                    onSelect={() => handleSelectThread(thread.id)}
                    onAddToCrm={() => handleAddToCrmFromThread(thread)}
                    leadTitle={leadMeta?.title || undefined}
                    leadStage={leadMeta?.pipeline_stage || undefined}
                    leadRegion={leadMeta?.region || undefined}
                    highlighted={Boolean(highlighted)}
                    onDismiss={() => handleDismissThread(thread.id)}
                    dismissDisabled={Boolean(dismissingThreads[thread.id])}
                  />
                );
              })}
            </div>
          )}
        </aside>
        <div className="h-[70vh] min-h-[28rem]">
          <EmailThreadView
            thread={selectedThread}
            messages={messages}
            messageLoading={messagesLoading}
            comments={comments}
            commentsLoading={commentsLoading}
            tasks={linkedTasks}
            onReply={handleReply}
            onForward={handleForward}
            onCreateTask={handleCreateTaskFromThread}
            onAddComment={handleAddComment}
            lead={selectedLead}
            contact={threadContact}
            onOpenLead={() => {
              if (!selectedLead) return;
              navigate(`/booking?leadId=${selectedLead.id}`);
            }}
            onOpenContact={() => {
              if (!selectedThread?.contact_id) return;
              navigate(`/contacts?contact=${selectedThread.contact_id}`);
            }}
            onCreateLeadFromThread={async () => {
              if (!selectedThread) return;
              const defaultTitle = `Booking – ${selectedThread.participants?.[0] ?? "contact"} – ${
                selectedThread.subject || "email thread"
              }`;
              try {
                const lead = await createLeadFromEmailThread({
                  thread_id: selectedThread.id,
                  title: defaultTitle,
                });
                setSelectedLead(lead);
                setThreads((prev) =>
                  prev.map((thread) =>
                    thread.id === selectedThread.id ? { ...thread, lead_id: lead.id } : thread,
                  ),
                );
                push({ title: "Lead created", message: "Lead created from email thread." });
              } catch (error) {
                const message = error instanceof Error ? error.message : "Unable to create lead from thread.";
                push({ title: "Lead creation", message });
              }
            }}
            onLinkToExistingLead={() => {
              setLeadSearchOpen(true);
            }}
            onCreateContactFromThread={handleCreateContactFromThread}
            contactCandidates={threadContactCandidates}
            contactSearchValue={contactSearch}
            contactSearchResults={contactResults}
            contactSearching={contactSearching}
            contactLinking={contactLinking}
            contactLinkSuccess={contactLinkSuccess}
            onContactSearchChange={setContactSearch}
            onLinkExistingContact={handleLinkExistingContact}
            onOpenContactResult={(contactId) => navigate(`/contacts?contact=${contactId}`)}
            onCreateEventFromThread={handleOpenEventDrawer}
          />
        </div>
      </section>

      <TemplateManagerDrawer
        open={templateManagerOpen}
        onClose={() => setTemplateManagerOpen(false)}
        onTemplatesChanged={() => setTemplateRefreshKey((prev) => prev + 1)}
      />

      <BookingEventDrawer
        open={eventDrawerOpen}
        initialValues={eventInitialValues ?? undefined}
        submitting={eventSubmitting}
        onSubmit={handleCreateEvent}
        onClose={() => setEventDrawerOpen(false)}
      />

      <EmailComposerDrawer
        open={composerOpen}
        mode={composerMode}
        initial={composerInitial}
        contactId={activeComposerContactId}
        leadId={activeComposerLeadId}
        refreshKey={templateRefreshKey}
        onClose={() => setComposerOpen(false)}
        onOpenTemplateManager={() => setTemplateManagerOpen(true)}
        onSent={() => {
          void refreshThreads();
          if (selectedThreadId) {
            void (async () => {
              try {
                const payload = await listThreadMessages(selectedThreadId);
                setMessages(payload ?? []);
              } catch {
                // best-effort refresh
              }
            })();
          }
        }}
      />

      <section className="mt-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-xs text-primary">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-semibold">
            Want AI to draft and schedule outreach?
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full uppercase tracking-wide text-[11px]"
            onClick={() => {
              navigate("/agents/email");
            }}
          >
            Explore Email Agent
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-primary/90">
          Upgrade to unlock AI-assisted drafting, sequencing, and approvals. Manual workflows remain fully available here.
        </p>
      </section>
      <LeadSearchModal
        open={leadSearchOpen}
        onClose={() => setLeadSearchOpen(false)}
        onSelect={async (lead) => {
          if (!selectedThread) return;
          try {
            const updatedLead = await linkLeadEmailThread(lead.id, { thread_id: selectedThread.id });
            setSelectedLead(updatedLead);
            setThreads((prev) =>
              prev.map((thread) =>
                thread.id === selectedThread.id ? { ...thread, lead_id: updatedLead.id } : thread,
              ),
            );
            push({ title: "Lead linked", message: "Thread linked to lead." });
          } catch (error) {
            const message = error instanceof Error ? error.message : "Unable to link thread to lead.";
            push({ title: "Link failed", message });
          } finally {
            setLeadSearchOpen(false);
          }
        }}
        initialQuery={selectedThread?.subject || selectedThread?.participants?.[0]}
      />

    </div>
  );
}

function formatTimestamp(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

type ParsedAddress = {
  name: string | null;
  email: string | null;
};

function parseMailboxAddress(raw?: string | null): ParsedAddress {
  if (!raw) {
    return { name: null, email: null };
  }
  const bracketMatch = raw.match(/^(.*)<([^>]+)>$/);
  if (bracketMatch) {
    return {
      name: bracketMatch[1]?.trim().replace(/(^"|"$)/g, "") || null,
      email: bracketMatch[2]?.trim() || null,
    };
  }
  const emailGuess = raw.includes("@") ? raw.trim() : null;
  const inferredName = emailGuess ? raw.replace(emailGuess, "").trim() : raw.trim();
  return {
    name: inferredName || null,
    email: emailGuess,
  };
}

function pickThreadContact(thread: EmailThreadSummary, messages: EmailThreadMessage[]): ParsedAddress {
  const inbound = [...messages].reverse().find((message) => message.direction === "inbound" && message.from_address);
  if (inbound?.from_address) {
    return parseMailboxAddress(inbound.from_address);
  }
  const lastFrom = [...messages].reverse().find((message) => message.from_address);
  if (lastFrom?.from_address) {
    return parseMailboxAddress(lastFrom.from_address);
  }
  const participant = thread.participants?.[0];
  return parseMailboxAddress(participant || null);
}

function findLinkableMessage(messages: EmailThreadMessage[]): EmailThreadMessage | null {
  if (!messages.length) return null;
  const inbound = [...messages].reverse().find((message) => message.direction === "inbound");
  return inbound ?? messages[messages.length - 1] ?? null;
}
